import {
  IAddress,
  IMasterName,
  IPhoneNumber,
  ISocialMedia,
  MasterNameType,
  PhoneNumberType,
} from '@/models';
import { getAddressLocation, isValidStructuredAddress } from '@/services';

import { getFormattedDate } from './datetime';

type MasterNameLike = Partial<IMasterName> | null | undefined;
const PERSON_MISSING_SECONDARY_INFO = 'No DOB or address';
const ORGANIZATION_MISSING_SECONDARY_INFO = 'No address';
const phoneNumberPriority: Partial<Record<PhoneNumberType, number>> = {
  [PhoneNumberType.MOBILE]: 0,
  [PhoneNumberType.HOME]: 1,
  [PhoneNumberType.WORK]: 2,
  [PhoneNumberType.OTHER]: 3,
  [PhoneNumberType.CALLER]: 4,
};

export interface PersonNameParts {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  suffix?: string | null;
  name?: string | null;
  fullName?: string | null;
  type?: MasterNameType | string | null;
}

export interface FormatPersonNameOptions {
  includeMiddle?: boolean;
  includeSuffix?: boolean;
  fallback?: string;
}

const normalizeNamePart = (part?: string | null) =>
  typeof part === 'string' ? part.trim() : '';

export const formatPersonName = (
  person: PersonNameParts | null | undefined,
  options: FormatPersonNameOptions = {},
): string => {
  const {
    includeMiddle = true,
    includeSuffix = false,
    fallback = '',
  } = options;

  if (!person) return fallback;

  if (person.type === MasterNameType.ORGANIZATION) {
    return normalizeNamePart(person.name) || fallback;
  }

  const lastName = normalizeNamePart(person.lastName);
  const firstName = normalizeNamePart(person.firstName);
  const middleName = includeMiddle ? normalizeNamePart(person.middleName) : '';
  const suffix = includeSuffix ? normalizeNamePart(person.suffix) : '';

  const core = [lastName, firstName, middleName].filter(Boolean).join(' ');

  if (!core) {
    return (
      normalizeNamePart(person.name) ||
      normalizeNamePart(person.fullName) ||
      fallback
    );
  }

  return suffix ? `${core} ${suffix}` : core;
};

export const formatPersonNameCanonical = (
  person: PersonNameParts | null | undefined,
  options: FormatPersonNameOptions = {},
): string => {
  const { includeMiddle = true, fallback = '' } = options;

  if (!person) return fallback;

  if (person.type === MasterNameType.ORGANIZATION) {
    return normalizeNamePart(person.name) || fallback;
  }

  const core = [
    normalizeNamePart(person.firstName),
    includeMiddle ? normalizeNamePart(person.middleName) : '',
    normalizeNamePart(person.lastName),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    core ||
    normalizeNamePart(person.name) ||
    normalizeNamePart(person.fullName) ||
    fallback
  );
};

export const getPersonInitials = (
  person: PersonNameParts | null | undefined,
): string => {
  if (!person) return '';

  if (person.type === MasterNameType.ORGANIZATION) {
    return normalizeNamePart(person.name).charAt(0).toUpperCase();
  }

  const first = normalizeNamePart(person.firstName).charAt(0);
  const last = normalizeNamePart(person.lastName).charAt(0);

  if (first || last) return `${first}${last}`.toUpperCase();

  const composed =
    normalizeNamePart(person.name) || normalizeNamePart(person.fullName);
  const tokens = composed.split(/\s+/).filter(Boolean);

  if (!tokens.length) return '';

  const firstToken = tokens[0].charAt(0);
  const lastToken =
    tokens.length > 1 ? tokens[tokens.length - 1].charAt(0) : '';

  return `${firstToken}${lastToken}`.toUpperCase();
};

export const getPersonNameSearchKey = (
  person: PersonNameParts | null | undefined,
): string => {
  if (!person) return '';

  return [person.firstName, person.middleName, person.lastName]
    .map(normalizeNamePart)
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const isAddressObject = (addressValue: unknown): addressValue is IAddress =>
  !!addressValue &&
  typeof addressValue === 'object' &&
  !Array.isArray(addressValue);

export const getMasterNameDisplayName = (masterName: MasterNameLike) =>
  formatPersonName(masterName as PersonNameParts | null | undefined);

export const getMasterNameAddress = (
  masterName: MasterNameLike,
): IAddress | undefined => {
  if (!masterName) return undefined;

  const primaryAddress = masterName.addresses?.find(isAddressObject);
  if (primaryAddress) {
    return primaryAddress;
  }

  return isAddressObject(masterName.address) ? masterName.address : undefined;
};

export const getMasterNamePrimaryAddressText = (
  masterName: MasterNameLike,
): string => {
  const address = getMasterNameAddress(masterName);

  if (!address) {
    return '';
  }

  if (isValidStructuredAddress(address)) {
    return getAddressLocation(address);
  }

  return address.normalized || address.addressLabel || '';
};

export const getMasterNameSecondaryInfo = (masterName: MasterNameLike) => {
  if (!masterName) return '';

  const address = getMasterNamePrimaryAddressText(masterName);

  if (masterName.type === MasterNameType.ORGANIZATION) {
    return address || ORGANIZATION_MISSING_SECONDARY_INFO;
  }

  const birthday =
    masterName.type === MasterNameType.PERSON ? masterName.birthday : '';
  const formattedDob = birthday ? getFormattedDate(birthday) : '';

  if (formattedDob && address) {
    return `${formattedDob} · ${address}`;
  }

  if (formattedDob || address) {
    return formattedDob || address;
  }

  if (masterName.type === MasterNameType.PERSON) {
    return PERSON_MISSING_SECONDARY_INFO;
  }

  return '';
};

export interface IMasterNamePhoneNumberOption extends IPhoneNumber {
  inputValue: string;
}

type SocialMediaLike = Pick<ISocialMedia, 'platform' | 'username'> | null;

export interface ISocialMediaFieldError {
  index: number;
  platformMissing: boolean;
  usernameMissing: boolean;
}

const normalizeSocialMediaPlatform = (platform?: string | null) =>
  typeof platform === 'string' ? platform.trim() : '';

export const normalizeSocialMediaUsername = (username?: string | null) =>
  typeof username === 'string' ? username.trim() : '';

const normalizeSocialMediaEntry = (
  socialMedia: SocialMediaLike | undefined,
): ISocialMedia | null => {
  const platform = normalizeSocialMediaPlatform(socialMedia?.platform);
  const username = normalizeSocialMediaUsername(socialMedia?.username);

  if (!platform || !username) {
    return null;
  }

  return {
    ...socialMedia,
    platform,
    username,
  };
};

export const normalizeSocialMediaEntries = (
  socialMedia: SocialMediaLike[] | undefined,
): ISocialMedia[] =>
  (socialMedia ?? []).reduce<ISocialMedia[]>((acc, item) => {
    const normalizedItem = normalizeSocialMediaEntry(item);

    if (normalizedItem) {
      acc.push(normalizedItem);
    }

    return acc;
  }, []);

export const getIncompleteSocialMediaFieldErrors = (
  socialMedia: SocialMediaLike[] | undefined,
) =>
  (socialMedia ?? []).reduce<ISocialMediaFieldError[]>((acc, item, index) => {
    const platform = normalizeSocialMediaPlatform(item?.platform);
    const username = normalizeSocialMediaUsername(item?.username);
    const platformMissing = !platform;
    const usernameMissing = !username;

    if (platformMissing && usernameMissing) {
      return acc;
    }

    if (platformMissing || usernameMissing) {
      acc.push({
        index,
        platformMissing,
        usernameMissing,
      });
    }

    return acc;
  }, []);

export const getDuplicateSocialMediaIndexes = (
  socialMedia: SocialMediaLike[] | undefined,
) => {
  const firstSeenByKey = new Map<string, number>();
  const duplicateIndexes = new Set<number>();

  (socialMedia ?? []).forEach((item, index) => {
    const normalizedItem = normalizeSocialMediaEntry(item);

    if (!normalizedItem) {
      return;
    }

    const normalizedUsername = normalizedItem.username.toLowerCase();
    const duplicateKey = `${normalizedItem.platform}::${normalizedUsername}`;
    const firstSeenIndex = firstSeenByKey.get(duplicateKey);

    if (firstSeenIndex === undefined) {
      firstSeenByKey.set(duplicateKey, index);
      return;
    }

    duplicateIndexes.add(firstSeenIndex);
    duplicateIndexes.add(index);
  });

  return [...duplicateIndexes].sort((a, b) => a - b);
};

export const getMasterNamePhoneNumbersByPriority = (
  masterName: MasterNameLike,
) => {
  const phoneNumbers = (masterName?.phoneNumbers || [])
    .filter((phoneNumber): phoneNumber is IPhoneNumber =>
      Boolean(phoneNumber?.phoneNumber),
    )
    .map((phoneNumber) => ({
      ...phoneNumber,
      inputValue: phoneNumber.phoneNumber.replace(/^\+1/, ''),
    }))
    .sort((a, b) => {
      // An explicitly flagged primary wins; rows without the flag fall through
      // to the type-priority ordering (backward-compatible with legacy data).
      const aPrimary = a.isPrimary === true ? 0 : 1;
      const bPrimary = b.isPrimary === true ? 0 : 1;
      if (aPrimary !== bPrimary) return aPrimary - bPrimary;

      const aPriority = phoneNumberPriority[a.type] ?? Number.MAX_SAFE_INTEGER;
      const bPriority = phoneNumberPriority[b.type] ?? Number.MAX_SAFE_INTEGER;

      return aPriority - bPriority;
    });

  return {
    primaryPhoneNumber: phoneNumbers[0] as
      | IMasterNamePhoneNumberOption
      | undefined,
    additionalPhoneNumbers: phoneNumbers.slice(
      1,
    ) as IMasterNamePhoneNumberOption[],
  };
};

/**
 * Normalize a US phone input to the stored `+1XXXXXXXXXX` form (exactly 12
 * characters, matching the backend `^\+1[0-9]{10}$` constraint). Returns '' for
 * anything that is not a 10-digit US number, so callers never persist an
 * invalid length.
 */
export const normalizeUsPhone = (value?: string | null): string => {
  if (!value) return '';
  const digits = String(value)
    .replace(/\D/g, '')
    .replace(/^1(?=\d{10}$)/, '');
  return digits.length === 10 ? `+1${digits}` : '';
};

/**
 * Return a new phoneNumbers array where `rawPhoneNumber` is present exactly once
 * and is the only primary. Dedupes against existing entries by normalized
 * number (a match keeps its type but becomes primary); all others are demoted.
 * Invalid input returns the list unchanged. Guarantees at most one primary,
 * which the backend requires.
 */
export const setPrimaryPhoneNumber = (
  phoneNumbers: IPhoneNumber[] | null | undefined,
  rawPhoneNumber: string,
  type: PhoneNumberType = PhoneNumberType.OTHER,
): IPhoneNumber[] => {
  const normalized = normalizeUsPhone(rawPhoneNumber);
  const existing = phoneNumbers ?? [];
  if (!normalized) return existing;

  let found = false;
  const next = existing.map((phone) => {
    if (normalizeUsPhone(phone.phoneNumber) === normalized) {
      found = true;
      return { ...phone, phoneNumber: normalized, isPrimary: true };
    }
    return { ...phone, isPrimary: false };
  });

  if (!found) next.push({ phoneNumber: normalized, type, isPrimary: true });
  return next;
};
